import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { i18n } from '@/shared/i18n';

/**
 * The top bar's title and subtitle are owned by whichever screen is mounted, not
 * by the shell. A screen calls {@link usePageChrome} in an effect to publish its
 * title; the shell reads the current value to render the top bar (UI spec §2.2).
 * A tiny context is enough — there is no cross-feature client state that would
 * justify a store (design.md, "State ownership").
 */
export interface PageChrome {
  readonly title: string;
  readonly subtitle?: string;
}

interface PageChromeContextValue {
  readonly chrome: PageChrome;
  readonly setChrome: (chrome: PageChrome) => void;
}

const PageChromeContext = createContext<PageChromeContextValue | null>(null);

export function PageChromeProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [chrome, setChrome] = useState<PageChrome>({ title: i18n.t('app.name') });
  return (
    <PageChromeContext.Provider value={{ chrome, setChrome }}>{children}</PageChromeContext.Provider>
  );
}

export function usePageChromeValue(): PageChrome {
  const context = useContext(PageChromeContext);
  if (!context) {
    throw new Error('usePageChromeValue must be used within a PageChromeProvider');
  }
  return context.chrome;
}

/** A screen publishes its top-bar title and subtitle for as long as it is mounted. */
export function usePageChrome(chrome: PageChrome): void {
  const context = useContext(PageChromeContext);
  if (!context) {
    throw new Error('usePageChrome must be used within a PageChromeProvider');
  }
  const { setChrome } = context;
  const { title, subtitle } = chrome;
  useEffect(() => {
    setChrome({ title, ...(subtitle !== undefined ? { subtitle } : {}) });
  }, [setChrome, title, subtitle]);
}
